import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import { Plus, MagnifyingGlass, Storefront, FunnelSimple } from '@phosphor-icons/react';
import { MarketplaceCard } from '../components/MarketplaceCard';
import { API, CATEGORY_LABELS, CONDITION_LABELS } from '../utils/marketplace';
import { setPageMeta } from '../utils/seo';
import { AmazonPicks } from '../components/AmazonPicks';

export default function Marketplace() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [searchInput, setSearchInput] = useState(params.get('q') || '');

  const filters = {
    q: params.get('q') || '',
    category: params.get('category') || '',
    condition: params.get('condition') || '',
    city: params.get('city') || '',
    state: params.get('state') || '',
    min_price: params.get('min_price') || '',
    max_price: params.get('max_price') || '',
    sort: params.get('sort') || 'newest',
    page: parseInt(params.get('page') || '1', 10),
  };

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    if (key !== 'page') next.delete('page');
    setParams(next);
  };

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const qp = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) qp[k] = v; });
      const res = await axios.get(`${API}/api/marketplace/listings`, { params: qp });
      setListings(res.data.listings);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (e) {
      setListings([]);
    } finally {
      setLoading(false);
    }
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchListings(); }, [fetchListings]);

  useEffect(() => {
    setPageMeta({
      title: 'Tools & Materials Marketplace — Buy & Sell New and Used | ConstructHelp',
      description: 'Buy and sell new and used tools, leftover building materials, and equipment from local contractors, workers, and homeowners. Power tools, lumber, plumbing, electrical and more.',
      url: `${window.location.origin}/marketplace`,
    });
  }, []);

  const goSell = () => {
    const token = localStorage.getItem('token');
    navigate(token ? '/marketplace/new' : '/login?redirect=/marketplace/new');
  };

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900" data-testid="marketplace-page">
      {/* Header */}
      <div className="bg-[#0F172A] border-b-4 border-black py-10">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-xs font-black uppercase tracking-widest text-[#D95800] mb-2 flex items-center gap-1.5">
                <Storefront size={16} weight="fill" /> Marketplace
              </p>
              <h1 className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight">
                Tools & Materials
              </h1>
              <p className="text-neutral-400 mt-2 text-sm md:text-base">
                Buy and sell new & used tools, leftover materials, and equipment — from real pros and homeowners near you.
              </p>
            </div>
            <button
              onClick={goSell}
              className="inline-flex items-center gap-2 bg-[#D95800] text-white font-black uppercase text-sm px-6 py-3 border-2 border-black shadow-[4px_4px_0px_#000] hover:-translate-y-0.5 transition-transform"
              data-testid="marketplace-sell-btn"
            >
              <Plus size={16} weight="bold" /> Sell an Item
            </button>
          </div>

          {/* Search */}
          <form
            onSubmit={(e) => { e.preventDefault(); setFilter('q', searchInput); }}
            className="mt-6 flex gap-2 max-w-xl"
          >
            <div className="relative flex-1">
              <MagnifyingGlass size={18} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search dewalt drill, lumber, copper pipe..."
                className="w-full pl-10 pr-3 py-2.5 bg-white dark:bg-neutral-800 border-2 border-black rounded-lg text-sm font-medium text-neutral-900 dark:text-white focus:outline-none"
                data-testid="marketplace-search-input"
              />
            </div>
            <button type="submit" className="bg-[#2B4C6F] text-white font-black uppercase text-xs px-5 border-2 border-black rounded-lg" data-testid="marketplace-search-btn">
              Search
            </button>
          </form>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Category chips */}
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" data-testid="marketplace-category-chips">
          <button
            onClick={() => setFilter('category', '')}
            className={`shrink-0 text-xs font-black uppercase px-3 py-1.5 border-2 border-black rounded-full transition-colors ${!filters.category ? 'bg-[#0F172A] text-white' : 'bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300'}`}
          >
            All
          </button>
          {Object.entries(CATEGORY_LABELS).map(([slug, label]) => (
            <button
              key={slug}
              onClick={() => setFilter('category', filters.category === slug ? '' : slug)}
              className={`shrink-0 text-xs font-black uppercase px-3 py-1.5 border-2 border-black rounded-full transition-colors ${filters.category === slug ? 'bg-[#D95800] text-white' : 'bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300'}`}
              data-testid={`marketplace-cat-${slug}`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center gap-1.5 text-xs font-black uppercase px-3 py-1.5 bg-white dark:bg-neutral-800 border-2 border-black rounded-lg"
            data-testid="marketplace-filters-toggle"
          >
            <FunnelSimple size={14} weight="bold" /> Filters
          </button>
          <select
            value={filters.sort}
            onChange={(e) => setFilter('sort', e.target.value)}
            className="text-xs font-bold px-3 py-1.5 bg-white dark:bg-neutral-800 border-2 border-black rounded-lg text-neutral-700 dark:text-neutral-300"
            data-testid="marketplace-sort-select"
          >
            <option value="newest">Newest</option>
            <option value="price_asc">Price: Low to High</option>
            <option value="price_desc">Price: High to Low</option>
          </select>
          <p className="text-xs text-neutral-500 ml-auto" data-testid="marketplace-result-count">{total} item{total === 1 ? '' : 's'}</p>
          <button
            onClick={async () => {
              const token = localStorage.getItem('token');
              if (!token) { navigate(`/login?redirect=/marketplace`); return; }
              const payload = {};
              ['q', 'category', 'condition', 'city', 'state'].forEach((k) => { if (filters[k]) payload[k] = filters[k]; });
              if (filters.max_price) payload.max_price = Number(filters.max_price);
              try {
                const axios = (await import('axios')).default;
                await axios.post(`${API}/api/marketplace/saved-searches`, payload, { headers: { Authorization: `Bearer ${token}` } });
                window?.dispatchEvent(new Event('saved-search'));
                const { toast } = await import('sonner');
                toast.success("Search saved — we'll email you when matching items are listed");
              } catch (e) {
                const { toast } = await import('sonner');
                toast.error(e.response?.data?.detail || 'Could not save search');
              }
            }}
            className="inline-flex items-center gap-1.5 text-xs font-black uppercase px-3 py-1.5 bg-[#0F172A] text-white border-2 border-black rounded-lg"
            data-testid="save-search-btn"
          >
            🔔 Save Search
          </button>
        </div>

        {showFilters && (
          <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-2 bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-3" data-testid="marketplace-filters-panel">
            <select
              value={filters.condition}
              onChange={(e) => setFilter('condition', e.target.value)}
              className="text-xs font-bold px-2 py-2 border-2 border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-900 text-neutral-700 dark:text-neutral-300"
              data-testid="marketplace-condition-filter"
            >
              <option value="">Any Condition</option>
              {Object.entries(CONDITION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <input
              defaultValue={filters.city}
              onBlur={(e) => setFilter('city', e.target.value)}
              placeholder="City"
              className="text-xs font-bold px-2 py-2 border-2 border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-900 text-neutral-700 dark:text-neutral-300"
              data-testid="marketplace-city-filter"
            />
            <input
              defaultValue={filters.state}
              onBlur={(e) => setFilter('state', e.target.value)}
              placeholder="State"
              className="text-xs font-bold px-2 py-2 border-2 border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-900 text-neutral-700 dark:text-neutral-300"
              data-testid="marketplace-state-filter"
            />
            <input
              type="number" min="0"
              defaultValue={filters.min_price}
              onBlur={(e) => setFilter('min_price', e.target.value)}
              placeholder="Min $"
              className="text-xs font-bold px-2 py-2 border-2 border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-900 text-neutral-700 dark:text-neutral-300"
              data-testid="marketplace-minprice-filter"
            />
            <input
              type="number" min="0"
              defaultValue={filters.max_price}
              onBlur={(e) => setFilter('max_price', e.target.value)}
              placeholder="Max $"
              className="text-xs font-bold px-2 py-2 border-2 border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-900 text-neutral-700 dark:text-neutral-300"
              data-testid="marketplace-maxprice-filter"
            />
          </div>
        )}

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-neutral-200 dark:bg-neutral-800 border-2 border-neutral-300 dark:border-neutral-700 rounded-xl aspect-[3/4] animate-pulse" />
            ))}
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-20" data-testid="marketplace-empty-state">
            <Storefront size={56} weight="duotone" className="text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
            <p className="font-black uppercase text-neutral-700 dark:text-neutral-300">No items found</p>
            <p className="text-sm text-neutral-500 mt-1 mb-5">Be the first to list something in this category.</p>
            <button onClick={goSell} className="inline-flex items-center gap-2 bg-[#D95800] text-white font-black uppercase text-sm px-6 py-3 border-2 border-black shadow-[4px_4px_0px_#000]">
              <Plus size={16} weight="bold" /> Sell an Item
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-6" data-testid="marketplace-grid">
            {listings.map((l) => <MarketplaceCard key={l._id} listing={l} />)}
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex justify-center gap-2 mt-8">
            {[...Array(pages)].map((_, i) => (
              <button
                key={i}
                onClick={() => setFilter('page', String(i + 1))}
                className={`w-9 h-9 text-sm font-black border-2 border-black rounded-lg ${filters.page === i + 1 ? 'bg-[#0F172A] text-white' : 'bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300'}`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}

        {/* SEO category links */}
        <div className="mt-14 pt-6 border-t-2 border-neutral-200 dark:border-neutral-700" data-testid="marketplace-category-links">
          <h2 className="text-sm font-black uppercase text-neutral-500 mb-3">Browse by category</h2>
          <div className="flex flex-wrap gap-2">
            {Object.entries(CATEGORY_LABELS).map(([k, label]) => (
              <RouterLink
                key={k}
                to={`/marketplace/category/${k}`}
                className="text-xs font-black uppercase px-3 py-1.5 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 border-2 border-black rounded-full hover:bg-[#D95800] hover:text-white transition-colors"
              >
                {label}
              </RouterLink>
            ))}
          </div>
        </div>
        <AmazonPicks />
      </div>
    </div>
  );
}
